import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  OnboardingProgress,
  useOnboardingStore,
} from 'state/store/onboarding/use-onboarding-store';
import {
  getConversationPreferences,
  isPreferencesForbidden,
  putConversationPreferences,
  type ConversationPreferences,
  type ConversationPreferencesPatch,
} from 'features/conversation-preferences/services/conversation-preferences-service';
import { hasAddressForms } from 'features/conversation-preferences/address-form';
import { useConversationPreferences } from 'features/conversation-preferences/hooks/use-conversation-preferences';
import {
  completeOnboardingPreferences,
  onboardingCompletionAnswers,
  onlyMissingPreferences,
  progressPreferences,
  saveOnboardingPreferences,
} from './onboarding-preferences';
import { needsSalutationChoice } from './onboarding-salutation';
import { ONBOARDING_CONVERSATION_PREFIX, onboardingLanguage } from './onboarding-script';

/** Answers kept under the same names in the saved progress. */
const PROGRESS_ANSWER_KEYS = [
  'addressForm',
  'givenName',
  'familyName',
  'salutation',
  'salutationText',
  'salutationInferred',
  'industry',
  'industryText',
  'catchUp',
] as const;

/** Answers carried in router state so later steps can reference them. */
export interface OnboardingAnswers
  extends Pick<OnboardingProgress, (typeof PROGRESS_ANSWER_KEYS)[number]> {
  onboardingName?: string;
  onboardingTeam?: string;
  onboardingTools?: string;
  /** Set once the last step is answered; the journey stays open until the user picks an action. */
  onboardingCompleted?: boolean;
}

/** Name, industry, area and tools. On the chat page step 1 is the salutation, a sub-step of
 *  the name asked only when the name left it open. */
export const ONBOARDING_TOTAL_STEPS = 4;
export const ONBOARDING_SALUTATION_STEP = 1;
export const ONBOARDING_INDUSTRY_STEP = 2;
export const ONBOARDING_AREA_STEP = 3;
export const ONBOARDING_TOOLS_STEP = 4;

/** The catch-up is put off at most this often, then it is not offered again. */
export const ONBOARDING_CATCH_UP_MAX_SKIPS = 2;

/** Query params added to /chat/:id when a conversation continues onboarding:
 *  /chat/:id?journey=onboarding&step=2 */
export const JOURNEY_PARAM = 'journey';
export const JOURNEY_STEP_PARAM = 'step';
export const ONBOARDING_JOURNEY = 'onboarding';

export const buildOnboardingChatUrl = (conversationId: string, step: number) =>
  `/chat/${conversationId}?${JOURNEY_PARAM}=${ONBOARDING_JOURNEY}&${JOURNEY_STEP_PARAM}=${step}`;

/** True on /onboarding or any page with ?journey=onboarding. Plain function so code
 *  outside the router (the global Buddy) can check window.location. */
export const isOnboardingLocation = ({ pathname, search }: { pathname: string; search: string }) =>
  pathname === '/onboarding' ||
  new URLSearchParams(search).get(JOURNEY_PARAM) === ONBOARDING_JOURNEY;

const readOnboardingProgress = (userId: string): OnboardingProgress | null =>
  useOnboardingStore.getState().progressByUser[userId] ?? null;

const saveProgress = (userId: string, changes: OnboardingProgress) => {
  const progress = readOnboardingProgress(userId);
  const next = { ...progress, ...changes };
  if (progress && JSON.stringify(next) === JSON.stringify(progress)) return;
  useOnboardingStore.getState().setProgress(userId, next);
  // The store is what the app reads. The preference also keeps the step, so another device can
  // resume. Best effort: a failed or refused write never blocks the journey.
  const patch = progressPreferences(progress, next);
  if (Object.keys(patch).length) void saveOnboardingPreferences(patch);
};

/** The progress another device saved in the preference, as the fields the journey reads. */
const progressFromPreferences = (preferences: ConversationPreferences): OnboardingProgress => {
  const fields = {
    finishedStep: preferences.onboardingStep,
    name: preferences.givenName,
    givenName: preferences.givenName,
    familyName: preferences.familyName,
    addressForm: preferences.addressForm === 'unknown' ? null : preferences.addressForm,
    salutation: preferences.salutation,
    salutationText: preferences.salutationText,
    salutationInferred: preferences.salutationInferred,
    industry: preferences.industry,
    industryText: preferences.industryText,
    team: preferences.workArea,
    tools: preferences.tools,
    catchUpSkips: preferences.catchUpSkips,
  };
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value != null)
  ) as OnboardingProgress;
};

/** Run once on login. The preference replaces local progress when another device got further,
 *  so the journey resumes there. Nothing is written. A finished current onboarding needs no
 *  local progress (the caller closes it); a finished old one only has the catch-up left. */
const restoreProgressFromPreferences = (userId: string, preferences: ConversationPreferences) => {
  const localProgress = readOnboardingProgress(userId);
  const version = preferences.onboardingVersion ?? 0;
  if (version >= 2) return;
  if (version === 1) {
    if (!localProgress) {
      useOnboardingStore.getState().setProgress(userId, { finishedStep: LEGACY_TOTAL_STEPS });
    }
    return;
  }
  const remoteProgress = progressFromPreferences(preferences);
  if ((remoteProgress.finishedStep ?? 0) > (localProgress?.finishedStep ?? 0)) {
    useOnboardingStore.getState().setProgress(userId, { ...localProgress, ...remoteProgress });
  }
};

const answersFromProgress = (progress: OnboardingProgress | null): OnboardingAnswers => ({
  onboardingName: progress?.name,
  onboardingTeam: progress?.team,
  onboardingTools: progress?.tools,
  onboardingCompleted: progress?.completed,
  ...Object.fromEntries(PROGRESS_ANSWER_KEYS.map((key) => [key, progress?.[key]])),
});

/** The old three-step journey finished at step 3 and, before `completed` existed, kept only
 *  `{ finishedStep: 3 }`. The current journey only gets past step 2 with an industry. */
const LEGACY_TOTAL_STEPS = 3;

export const isOnboardingComplete = (progress?: OnboardingProgress | null) =>
  Boolean(progress?.completed) ||
  (progress?.finishedStep ?? 0) >= ONBOARDING_TOTAL_STEPS ||
  (!progress?.industry && (progress?.finishedStep ?? 0) >= LEGACY_TOTAL_STEPS);

/** The first unanswered step from the answers themselves, so progress saved by the old
 *  three-step journey resumes at the right question. 0 is the name on /onboarding. */
export const getOnboardingResumeStep = (progress: OnboardingProgress) => {
  if (!progress.name) return 0;
  if (needsSalutationChoice(progress)) return ONBOARDING_SALUTATION_STEP;
  if (!progress.industry) return ONBOARDING_INDUSTRY_STEP;
  if (!progress.team) return ONBOARDING_AREA_STEP;
  return ONBOARDING_TOOLS_STEP;
};

const resumePathFor = (progress: OnboardingProgress) => {
  const step = getOnboardingResumeStep(progress);
  if (step === 0 || !progress.conversationId) return '/onboarding';
  return buildOnboardingChatUrl(progress.conversationId, step);
};

/** Where to send the user after login: the first unfinished step, or null when it's done or
 *  was already shown during this login. No saved progress starts the journey from step 1. */
export const getOnboardingResumePath = (userId: string, loginCount: number) => {
  const progress = readOnboardingProgress(userId);
  if (isOnboardingComplete(progress)) return null;
  if (loginCount <= (progress?.shownAtLoginCount ?? 0)) return null;
  if (!progress) return '/onboarding';
  // Progress from another device has no local conversation yet; the chat page rebuilds the
  // transcript from the saved answers, as it does for the catch-up.
  if (progress.name && !progress.conversationId) {
    saveProgress(userId, { conversationId: `${ONBOARDING_CONVERSATION_PREFIX}${uuidv4()}` });
  }
  return resumePathFor(readOnboardingProgress(userId) ?? progress);
};

export const markOnboardingShown = (userId: string, loginCount: number) =>
  saveProgress(userId, { shownAtLoginCount: loginCount });

/** Recent Chats row while the journey is unfinished. Gone once the last step is done. */
export const getOnboardingRecentChatPath = (progress?: OnboardingProgress): string | null => {
  if (!progress || isOnboardingComplete(progress)) return null;
  return resumePathFor(progress);
};

/**
 * Run once per login. Reads the shared preference (which also holds another device's progress),
 * then decides:
 * - `onboardingVersion >= 2` (on any device or tab): nothing, and the local journey is closed.
 * - unfinished: the resume path, as before.
 * - finished, but the preference doesn't say so: the old onboarding gets the one-time catch-up
 *   (after saving `onboardingVersion: 1`, so the Klax app offers the catch-up too); the current
 *   one only lost its last write, which is sent again for the fields the preference still lacks.
 * Returns null when nothing should open, also when progress or the preference can't be read.
 */
export const getOnboardingLoginPath = async (
  userId: string,
  loginCount: number
): Promise<string | null> => {
  let preferences: ConversationPreferences | null = null;
  try {
    preferences = await getConversationPreferences();
  } catch (error) {
    // Whether it was finished elsewhere is unknown, so only a journey already started here
    // goes on. A refused session (403) keeps no preference and starts like a new user.
    if (!isPreferencesForbidden(error) && !readOnboardingProgress(userId)) return null;
  }
  if (preferences) restoreProgressFromPreferences(userId, preferences);

  const progress = readOnboardingProgress(userId);
  if ((preferences?.onboardingVersion ?? 0) >= 2) {
    closeOnboardingDoneElsewhere(userId);
    return null;
  }
  if (!progress || !isOnboardingComplete(progress))
    return getOnboardingResumePath(userId, loginCount);
  if (!preferences) return null;

  if (progress.industry) {
    // Only what the preference still lacks: values saved since (e.g. in settings) stay.
    void completeOnboardingPreferences(progress, preferences);
    return null;
  }
  if (preferences.onboardingVersion == null) {
    await putConversationPreferences({ onboardingVersion: 1 }, 'onboarding').catch((error) =>
      console.error('Failed to mark the earlier onboarding as done:', error)
    );
  }
  if ((progress.catchUpSkips ?? 0) >= ONBOARDING_CATCH_UP_MAX_SKIPS) return null;
  if (loginCount <= (progress.shownAtLoginCount ?? 0)) return null;
  return openOnboardingCatchUp(userId, preferences);
};

/**
 * A settings save for a user whose current onboarding is finished here but whose completion
 * write never arrived also marks it done (`onboardingVersion: 2`), so the next login doesn't
 * re-send the old answers over it. Only when nothing the completion would deliver is still
 * missing on the server (or sent by this save), so no onboarding answer is lost.
 */
export function withOnboardingDone(
  userId: string | undefined,
  patch: ConversationPreferencesPatch,
  stored?: ConversationPreferences | null
): ConversationPreferencesPatch {
  const progress = userId ? readOnboardingProgress(userId) : null;
  if (!stored || (stored.onboardingVersion ?? 0) >= 2) return patch;
  if (!progress?.industry || progress.catchUp || !isOnboardingComplete(progress)) return patch;
  const missing = onlyMissingPreferences(onboardingCompletionAnswers(progress), stored);
  // A null answer (e.g. no salutation in du mode) on a field the server lacks delivers nothing.
  const undelivered = (Object.keys(missing) as (keyof ConversationPreferencesPatch)[]).filter(
    (field) => missing[field] != null && !(field in patch)
  );
  return undelivered.length ? patch : { ...patch, onboardingVersion: 2 };
}

const COMPLETED_HERE_KEY = 'onboarding-completed-here';

const readCompletedHere = (): string[] => {
  try {
    const ids: unknown = JSON.parse(sessionStorage.getItem(COMPLETED_HERE_KEY) ?? '[]');
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

/** Journeys this tab finished itself, by conversation id. Its own completion writes
 *  `onboardingVersion: 2`, and changing an answer afterwards ([Back] from the completion card)
 *  reopens a step; that must not read as finished elsewhere. Per tab, so other tabs keep C12. */
export const markOnboardingCompletedHere = (conversationId: string) => {
  const ids = readCompletedHere();
  if (ids.includes(conversationId)) return;
  try {
    sessionStorage.setItem(COMPLETED_HERE_KEY, JSON.stringify([...ids, conversationId]));
  } catch {
    /* Storage blocked: this tab treats its own reopened journey as finished elsewhere. */
  }
};

export const wasOnboardingCompletedHere = (conversationId?: string) =>
  Boolean(conversationId && readCompletedHere().includes(conversationId));

/** The preference says the current onboarding is done, on another tab or in the Klax app: the
 *  local journey closes, unless this tab finished it itself, so /onboarding and the Recent Chats
 *  row go away (C12). */
export const closeOnboardingDoneElsewhere = (userId: string) => {
  const progress = readOnboardingProgress(userId);
  if (wasOnboardingCompletedHere(progress?.conversationId)) return;
  if (progress && !isOnboardingComplete(progress)) {
    saveProgress(userId, { completed: true, finishedStep: ONBOARDING_TOTAL_STEPS });
  }
};

/** True once the cached preference (read again on focus when stale) says the current
 *  onboarding is done; also closes the local journey. */
export const useOnboardingDoneElsewhere = () => {
  const userId = useCommonStore((state) => state.user?.id);
  const { data } = useConversationPreferences();
  const isDone = (data?.onboardingVersion ?? 0) >= 2;
  useEffect(() => {
    if (userId && isDone) closeOnboardingDoneElsewhere(userId);
  }, [userId, isDone]);
  return isDone;
};

/** The step a catch-up opens on: the du/Sie chips or the salutation, else the industry. */
export const onboardingCatchUpStep = (progress: OnboardingProgress) =>
  (!progress.addressForm && hasAddressForms(onboardingLanguage())) ||
  needsSalutationChoice(progress)
    ? ONBOARDING_SALUTATION_STEP
    : ONBOARDING_INDUSTRY_STEP;

/** The catch-up for users who finished the old onboarding, in a new local conversation that the
 *  chat page builds from the saved answers: the intro, then (in German, French and Italian) the
 *  du/Sie chips, otherwise straight to the industry question. Returns where it opens. */
const openOnboardingCatchUp = (userId: string, preferences: ConversationPreferences) => {
  const conversationId = `${ONBOARDING_CONVERSATION_PREFIX}${uuidv4()}`;
  markOnboardingCatchUpStarted(userId, conversationId, preferences);
  return buildOnboardingChatUrl(
    conversationId,
    onboardingCatchUpStep(readOnboardingProgress(userId) ?? {})
  );
};

/** The catch-up's own local conversation; it ends like the onboarding, at the completion card.
 *  What the preference already knows (e.g. from the Klax app) is not asked again. */
export const markOnboardingCatchUpStarted = (
  userId: string,
  conversationId: string,
  preferences?: ConversationPreferences
) => {
  const known: OnboardingProgress = {};
  if (preferences?.addressForm === 'formal' || preferences?.addressForm === 'informal') {
    known.addressForm = preferences.addressForm;
  }
  if (preferences?.givenName) known.givenName = preferences.givenName;
  if (preferences?.familyName) known.familyName = preferences.familyName;
  if (preferences?.salutation) {
    known.salutation = preferences.salutation;
    known.salutationText = preferences.salutationText ?? undefined;
  }
  saveProgress(userId, { ...known, catchUp: true, conversationId });
};

/** [Später]: nothing is written to the preference; it is offered again at the next login. */
export const skipOnboardingCatchUp = (userId: string) => {
  const progress = readOnboardingProgress(userId);
  saveProgress(userId, { catchUp: false, catchUpSkips: (progress?.catchUpSkips ?? 0) + 1 });
};

/** Navigation keeps progress; explicitly clearing an answer reopens the required steps. */
export const markOnboardingStepFinished = (
  userId: string,
  step: number,
  conversationId?: string,
  answers?: OnboardingAnswers
) => {
  const progress = readOnboardingProgress(userId);
  const restartsAnswers = answers?.onboardingCompleted === false && answers.onboardingTools === '';
  saveProgress(userId, {
    finishedStep: restartsAnswers ? step : Math.max(step, progress?.finishedStep ?? 0),
    conversationId: conversationId ?? progress?.conversationId,
    ...(answers?.onboardingName === undefined ? {} : { name: answers.onboardingName }),
    ...(answers?.onboardingTeam === undefined ? {} : { team: answers.onboardingTeam }),
    ...(answers?.onboardingTools === undefined ? {} : { tools: answers.onboardingTools }),
    ...(answers?.onboardingCompleted === undefined
      ? {}
      : { completed: answers.onboardingCompleted }),
    // A key that is present replaces the saved answer, so `undefined` clears it.
    ...Object.fromEntries(
      PROGRESS_ANSWER_KEYS.filter((key) => answers && key in answers).map((key) => [
        key,
        answers?.[key],
      ])
    ),
  });
  if (answers?.onboardingCompleted) {
    useOnboardingStore.getState().clearCompletionSession(userId);
  }
};

const mergeAnswers = (
  locationAnswers: OnboardingAnswers,
  progress: OnboardingProgress | null
): OnboardingAnswers => {
  const saved = answersFromProgress(progress);
  return {
    onboardingName: locationAnswers.onboardingName ?? saved.onboardingName,
    onboardingTeam: locationAnswers.onboardingTeam ?? saved.onboardingTeam,
    onboardingTools: locationAnswers.onboardingTools ?? saved.onboardingTools,
    onboardingCompleted: locationAnswers.onboardingCompleted ?? saved.onboardingCompleted,
    // Answers given on the chat page are saved before the URL changes, so the store is newer.
    ...Object.fromEntries(
      PROGRESS_ANSWER_KEYS.map((key) => [
        key,
        progress && key in progress ? progress[key] : locationAnswers[key],
      ])
    ),
  };
};

/** Read on the chat page to know the conversation came from onboarding. */
export const useOnboardingJourney = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const userId = useCommonStore((state) => state.user?.id);
  const progress = useOnboardingStore((state) =>
    userId ? (state.progressByUser[userId] ?? null) : null
  );
  const answers = mergeAnswers((location.state ?? {}) as OnboardingAnswers, progress);
  const isOnboarding = searchParams.get(JOURNEY_PARAM) === ONBOARDING_JOURNEY;
  const step = Number(searchParams.get(JOURNEY_STEP_PARAM)) || 1;

  // Past the last step the journey params are removed, which hides the onboarding UI.
  const setStep = (next: number, newAnswers?: OnboardingAnswers) => {
    const merged = { ...answers, ...newAnswers };
    // Moving on finishes the step before `next`; answering the last question finishes all.
    const finishedStep = merged.onboardingCompleted ? ONBOARDING_TOTAL_STEPS : next - 1;
    if (userId) markOnboardingStepFinished(userId, finishedStep, undefined, merged);

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next > ONBOARDING_TOTAL_STEPS) {
          params.delete(JOURNEY_PARAM);
          params.delete(JOURNEY_STEP_PARAM);
        } else {
          params.set(JOURNEY_STEP_PARAM, String(next));
        }
        return params;
      },
      { replace: true, preventScrollReset: true, state: merged }
    );
  };

  return { isOnboarding, step: isOnboarding ? step : null, answers, setStep };
};
